import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, PartyPopper, AlertCircle } from "lucide-react";
import api from "../lib/api";
import { SpeechBubble, MillieHero } from "../components/MascotBubble";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [state, setState] = useState("checking");
  const [plan, setPlan] = useState("");
  const tries = useRef(0);

  useEffect(() => {
    const paypal = params.get("paypal") === "1";
    const id = paypal ? params.get("subscription_id") : params.get("session_id");
    if (!id) { setState("error"); return; }
    let cancelled = false;
    const poll = async () => {
      try {
        const { data } = await api.get(paypal ? `/billing/paypal/status/${id}` : `/billing/status/${id}`);
        if (cancelled) return;
        if (data.payment_status === "paid") { setPlan(data.plan); setState("paid"); return; }
        if (["expired", "refunded"].includes(data.payment_status)) { setState("error"); return; }
      } catch { /* retry */ }
      if (++tries.current < 8) setTimeout(poll, 2000); else setState("slow");
    };
    poll();
    return () => { cancelled = true; };
  }, [params]);

  const name = plan === "plus" ? "Sorted+" : "Sorted";
  return (
    <div className="min-h-screen w-full bg-[#F7E9E6] flex justify-center">
      <div className="relative w-full max-w-md min-h-screen bg-gradient-to-b from-[#FFF6F1] to-[#FCE7EC] px-6 pt-16 text-center" data-testid="payment-success">
        <MillieHero mood={state === "paid" ? "celebrate" : "thinking"} entrance="celebrate" className="h-56 w-56 mx-auto" testId="payment-millie" />
        <div className="mt-4">
          <SpeechBubble tail="none" testId="payment-bubble">
            {state === "checking" && "Just confirming that with the bank… two ticks."}
            {state === "paid" && `You're on ${name}! Thank you — dinner's on me. Well, on you, but I'll do the thinking.`}
            {state === "slow" && "Payment's taking a moment to land. It'll show in your Profile shortly — no need to pay again."}
            {state === "error" && "Hmm, that payment didn't go through. Nothing's been charged — want to try again?"}
          </SpeechBubble>
        </div>
        <div className="mt-8 flex justify-center">
          {state === "checking" ? <Loader2 className="animate-spin text-sorted-rose" data-testid="payment-checking" /> :
            state === "paid" ? <PartyPopper className="text-sorted-rose" size={28} /> : <AlertCircle className="text-amber-500" size={28} />}
        </div>
        <button onClick={() => navigate(state === "error" ? "/plans" : "/home")} data-testid="payment-continue"
          className="mt-8 w-full bg-sorted-rose text-white font-bold py-4 rounded-full">{state === "error" ? "Back to plans" : "Back to my week"}</button>
      </div>
    </div>
  );
}

export function PaymentCancel() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen w-full bg-[#F7E9E6] flex justify-center">
      <div className="relative w-full max-w-md min-h-screen bg-gradient-to-b from-[#FFF6F1] to-[#FCE7EC] px-6 pt-16 text-center" data-testid="payment-cancel">
        <MillieHero mood="cosy" className="h-56 w-56 mx-auto" testId="cancel-millie" />
        <div className="mt-4"><SpeechBubble tail="none">No worries at all — nothing was charged. Free Sorted is still yours, and I'm still here.</SpeechBubble></div>
        <button onClick={() => navigate("/home")} data-testid="payment-cancel-home" className="mt-8 w-full bg-sorted-ink text-white font-bold py-4 rounded-full">Back to my week</button>
        <button onClick={() => navigate("/plans")} data-testid="payment-cancel-plans" className="mt-3 w-full text-sm font-bold text-sorted-slate">See plans again</button>
      </div>
    </div>
  );
}
